import React, { useState, useEffect, useRef, useContext } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    TablePagination,
    Select,
    MenuItem,
    Switch,
} from '@mui/material';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ExtractionObject, getObjectById, scoSearch, sdoSearch } from '../../../services/stixify_api.ts';
import { useAlert } from '../../../contexts/alert-context.tsx';
import { URLS } from '../../../services/urls.ts';
import { TeamRouteContext } from '../../team-layout.tsx/index.tsx';
import { getScoValue, updateURLWithParams } from '../../../services/utils.ts';
import { observable_types } from './object-types.js'

const PAGE_SIZE = 50


const ObservableSearchPage: React.FC = () => {
    const { teamId } = useContext(TeamRouteContext)
    const [reportData, setReportData] = useState<ExtractionObject[]>([])
    const [loading, setLoading] = useState<boolean>(false);
    const [value, setValue] = useState('');
    const [searchType, setSearchType] = useState('observable')
    const [type, setType] = useState('');
    const [objectId, setobjectId] = useState('')
    const [useObjectId, setUseObjectId] = useState(false);
    const [selectedType, setSelectType] = useState<{ field_name: string }>();
    const [pageData, setPageData] = useState({
        resultsCount: 0,
        page: 1,
    })
    const [initialDataLoaded, setInitialDataLoaded] = useState(false)
    const location = useLocation();
    const alert = useAlert()


    useEffect(() => {
        const query = new URLSearchParams(location.search);
        setValue(query.get('value') || '')
        setType(query.get('type') || '')
        setSearchType(query.get('searchType') || 'observable')
        setobjectId(query.get('objectId') || '')
        setUseObjectId(query.get('useObjectId') === 'true' || false)
        setInitialDataLoaded(true)
    }, [location])

    useEffect(() => {
        loadReports(1)
    }, [initialDataLoaded])

    useEffect(() => {
        updateURLWithParams({
            type, value, searchType, useObjectId, objectId
        })
    }, [type, value, searchType, useObjectId, objectId])

    const getExtractions = (page: number) => {
      if(useObjectId) {
        if(!objectId) return {
          data: {
            objects: [],
            page_number: 1,
            total_results_count: 0,
          }
        }
        return getObjectById(objectId)
      }
      if(searchType === 'observable') {
        return scoSearch(type, value, page);
      }
      return sdoSearch(type, value, page);
    }
    const loadReports = async (page: number) => {
        if(!initialDataLoaded) return
        setLoading(true)
        const res = await getExtractions(page)
        setReportData(res.data.objects)
        setPageData({
            page: res.data.page_number - 1,
            resultsCount: res.data.total_results_count,
        })
        setLoading(false)
    }

    const handlePageChange = async (event, page: number) => {
        loadReports(page + 1)
    }
    useEffect(() => {
        document.title = 'Intel Search | Obstracts Web'
    }, [])

    const onChangeSearchType = (searchType: string) => {
      setSearchType(searchType)
      setType('')
    }

    return (
        <Box p={4}>
            <Typography variant="h4" gutterBottom>
                Intel Search
            </Typography>
            <Typography>
                <p>Search for intelligence extracted from reports uploaded to Stixify. The search will return all extractions from any reports marked TLP:Green, and extractions from reports belonging to your team marked TLP:Amber.</p>
                <p>SDO and SCO refer to the STIX Object types, where SDO represent TTPs and more descriptive representations of a threat, and SCO represent IoCs or atomic indicators of a threat.</p>
            </Typography>
            <Box>
              <label>Search</label>
              <Switch checked={useObjectId} onChange={() => setUseObjectId(!useObjectId)}></Switch>
              <label>Use ID</label>
            </Box>
            {useObjectId ? <Box mb={5} sx={{display: 'flex'}}>
              <TextField
                    sx={{marginRight: '1rem', minWidth: '30rem'}}
                    type="value"
                    value={objectId}
                    onChange={(ev) => { setobjectId(ev.target.value) }}
                    placeholder="Object ID"
                    className="email-input"
                    label="Object ID"
                />
                <Button variant='contained' onClick={() => loadReports(1)}>filter</Button>
            </Box>: <Box mb={5} sx={{ display: 'flex' }}>
              <Box sx={{marginRight: '1rem'}}>
                <Typography>Search type</Typography>
                <Select
                    sx={{ minWidth: '10rem' }}
                    label="Type"
                    name="search_type"
                    value={searchType}
                    onChange={(ev) => onChangeSearchType(ev.target.value as string)}
                >
                  <MenuItem onClick={() => setSearchType('observable')} value='observable'>Observable</MenuItem>
                  <MenuItem onClick={() => setSearchType('object')} value='object'>Object</MenuItem>
                </Select>
              </Box>
              <Box sx={{marginRight: '1rem'}}>
                <Typography>Type</Typography>
                <Select
                    sx={{ minWidth: '20rem' }}
                    label="Type"
                    name="type"
                    value={type}
                    onChange={(ev) => { setType(ev.target.value) }}
                >
                    {observable_types.filter(observable_type => observable_type.type == (searchType === 'observable' ? 'SCO' : 'SDO')).map((object) => (
                        <MenuItem onClick={() => setSelectType(object)} key={object.value} value={object.value}>{object.label}</MenuItem>
                    ))}
                </Select>
              </Box>
              <Box>
              <Typography sx={{visibility: 'none'}}>.</Typography>
                <TextField
                    sx={{ marginLeft: '1rem', marginRight: '1rem' }}
                    type="value"
                    value={value}
                    onChange={(ev) => { setValue(ev.target.value) }}
                    placeholder={selectedType?.field_name || "value"}
                    className="email-input"
                    label={selectedType?.field_name || "value"}
                />
              </Box>
              <Box sx={{diaplay: 'flex', flexDirection: 'column'}}>
                <Typography sx={{visibility: 'none'}}>.</Typography>
                <Box sx={{display: 'flex', flex: 'auto'}}>
                  <Button variant='contained' onClick={() => loadReports(1)}>filter</Button>
                </Box>
              </Box>
            </Box>}
            {loading ? (
                <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
            ) : (
                <>
                    <TableContainer>
                        <Typography variant='h5'>Extractions (TTPs)</Typography>
                        <Typography>List of extractiotion objects for this filter</Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Value</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.map((sco) => (
                                    <TableRow key={sco.id}>
                                        <TableCell>{sco.type}</TableCell>
                                        <TableCell>{getScoValue(sco)}</TableCell>
                                        <TableCell><Link to={URLS.teamExtractionReports(teamId || '', sco.id, sco.type, getScoValue(sco))}>
                                            <Button variant='contained'> View reports</Button>
                                        </Link></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[]}
                            component="div"
                            count={pageData.resultsCount}
                            rowsPerPage={PAGE_SIZE}
                            page={pageData.page}
                            onPageChange={handlePageChange}
                            labelRowsPerPage={""} // Hide the rows per page label
                            style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}
                        />
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default ObservableSearchPage;